import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { customersBillingApi } from '@/api/customersBilling.api';
import type { CustomerPickerResponse, CustomerResponse } from '@/types/customer';

/** Fields needed at POS checkout from either full or picker customer shape. */
export type PosCheckoutCustomer = Pick<
  CustomerResponse,
  | 'id'
  | 'name'
  | 'phone'
  | 'walkIn'
  | 'walletBalance'
  | 'loyaltyPointsBalance'
  | 'loyaltyPoints'
  | 'creditEnabled'
  | 'creditAvailable'
  | 'creditLimit'
>;

const RECENT_CACHE_KEY = 'fattahone_recent_customers';

function loadRecentCache(): CustomerPickerResponse[] {
  try {
    const raw = localStorage.getItem(RECENT_CACHE_KEY);
    return raw ? (JSON.parse(raw) as CustomerPickerResponse[]) : [];
  } catch {
    return [];
  }
}

function saveRecentCache(customers: CustomerPickerResponse[]) {
  try {
    localStorage.setItem(RECENT_CACHE_KEY, JSON.stringify(customers.slice(0, 12)));
  } catch {
    /* ignore */
  }
}

export const fetchWalkInCustomer = createAsyncThunk('posCustomer/walkIn', () =>
  customersBillingApi.walkIn(),
);

export const fetchRecentCustomers = createAsyncThunk('posCustomer/recent', async () => {
  const server = await customersBillingApi.recent(8);
  const cached = loadRecentCache();
  const merged: CustomerPickerResponse[] = [];
  const seen = new Set<number>();
  for (const c of [...server, ...cached]) {
    if (c.walkIn || seen.has(c.id)) continue;
    seen.add(c.id);
    merged.push(c);
    if (merged.length >= 8) break;
  }
  return merged;
});

interface PosCustomerState {
  walkIn: CustomerResponse | null;
  selected: PosCheckoutCustomer | null;
  recent: CustomerPickerResponse[];
  searchResults: CustomerResponse[];
  searchLoading: boolean;
  initializing: boolean;
}

const initialState: PosCustomerState = {
  walkIn: null,
  selected: null,
  recent: loadRecentCache(),
  searchResults: [],
  searchLoading: false,
  initializing: true,
};

const posCustomerSlice = createSlice({
  name: 'posCustomer',
  initialState,
  reducers: {
    selectCustomer(state, action: PayloadAction<PosCheckoutCustomer | null>) {
      state.selected = action.payload;
      if (action.payload && !action.payload.walkIn) {
        const next = [
          action.payload,
          ...state.recent.filter((c) => c.id !== action.payload!.id),
        ].slice(0, 12);
        state.recent = next;
        saveRecentCache(next);
      }
    },
    setSearchResults(state, action: PayloadAction<CustomerResponse[]>) {
      state.searchResults = action.payload;
    },
    setSearchLoading(state, action: PayloadAction<boolean>) {
      state.searchLoading = action.payload;
    },
    clearSearch(state) {
      state.searchResults = [];
      state.searchLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWalkInCustomer.pending, (state) => {
        state.initializing = true;
      })
      .addCase(fetchWalkInCustomer.fulfilled, (state, action) => {
        state.walkIn = action.payload;
        if (!state.selected) {
          state.selected = action.payload;
        }
        state.initializing = false;
      })
      .addCase(fetchWalkInCustomer.rejected, (state) => {
        state.initializing = false;
      })
      .addCase(fetchRecentCustomers.fulfilled, (state, action) => {
        state.recent = action.payload;
        saveRecentCache(action.payload);
      });
  },
});

export const { selectCustomer, setSearchResults, setSearchLoading, clearSearch } =
  posCustomerSlice.actions;
export default posCustomerSlice.reducer;
